/* One delegated handler for every KODO "add straight to the cart" button:
   the homepage product cards and the upsell inside the cart drawer.

   The theme's <product-form-component> is not involved here — these buttons are
   not inside a product form. After the add we fire the theme's own `cart:update`
   event, which makes the cart drawer re-render itself from the server (so the
   upsell disappears once that pack is in the bag) and refreshes the header count.

   Loaded from both the products section and the header, so it guards against
   running twice. */
(function () {
  if (window.__kodoCartAdd) return;
  window.__kodoCartAdd = true;

  const shopRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* cart/add.js takes ~700ms, and asking for the drawer markup in the same
     request pushes it past a second. The drawer opens immediately, so until the
     response lands it was showing "Your cart is empty" — right after a tap on
     Add to cart, which reads as a failed add. Draw the line we already have the
     details for and let the real markup replace it. */
  const PENDING_STYLES = `
    .cart-drawer--kodo-adding .cart-drawer__heading--empty { display: none; }
    .kodo-pending { display: flex; gap: 14px; align-items: center; padding: 16px 0; }
    .kodo-pending__thumb { width: 64px; height: 64px; object-fit: cover; flex: 0 0 auto; background: #f1efe6; }
    .kodo-pending__text { min-width: 0; }
    .kodo-pending__title { font-size: 15px; font-weight: 500; margin: 0 0 4px; }
    .kodo-pending__price { font-size: 14px; opacity: .65; margin: 0; }
    .kodo-pending__title, .kodo-pending__price { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  `;

  function ensureStyles() {
    if (document.getElementById('kodo-pending-styles')) return;
    const style = document.createElement('style');
    style.id = 'kodo-pending-styles';
    style.textContent = PENDING_STYLES;
    document.head.appendChild(style);
  }

  function buildPendingRow(card) {
    const image = card.querySelector('img');
    const title = card.querySelector('.card-body h3');
    const price = card.querySelector('.price');

    const row = document.createElement('div');
    row.className = 'kodo-pending';
    row.setAttribute('aria-hidden', 'true');
    const thumb = document.createElement('img');
    thumb.className = 'kodo-pending__thumb';
    if (image) thumb.src = image.currentSrc || image.src;
    thumb.alt = '';
    const text = document.createElement('div');
    text.className = 'kodo-pending__text';
    const name = document.createElement('p');
    name.className = 'kodo-pending__title';
    name.textContent = title ? title.textContent.trim() : '';
    const cost = document.createElement('p');
    cost.className = 'kodo-pending__price';
    cost.textContent = price ? price.textContent.trim().replace(/\s+/g, ' ') : '';
    text.append(name, cost);
    row.append(thumb, text);
    return row;
  }

  // Returns a cleanup function, or null when there is nothing to draw from.
  function showPending(btn, drawer) {
    const card = btn.closest('.card');
    if (!card || !drawer) return null;

    ensureStyles();
    const row = buildPendingRow(card);
    let dialog = null;
    let cancelled = false;

    // On the first add the drawer's contents are not in the DOM yet — open()
    // renders them — so keep looking until they are. setTimeout rather than
    // requestAnimationFrame, which does not run while the tab is unpainted.
    let tries = 0;
    (function attach() {
      if (cancelled) return;
      dialog = drawer.querySelector('.cart-drawer__dialog');
      const items = drawer.querySelector('.cart-drawer__items');
      if (dialog && items) {
        dialog.classList.add('cart-drawer--kodo-adding');
        items.prepend(row);
        return;
      }
      if (++tries < 60) setTimeout(attach, 16);
    })();

    return () => {
      cancelled = true;
      row.remove();
      if (dialog) dialog.classList.remove('cart-drawer--kodo-adding');
    };
  }

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-card-add]');
    if (!btn || btn.disabled) return;

    event.preventDefault();

    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = btn.dataset.busyLabel || 'Adding…';

    // Open straight away. Waiting for the add to come back first made the
    // drawer feel like it lagged behind the click; it fills in a moment later.
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
    const clearPending = showPending(btn, drawer);

    const body = { id: Number(btn.dataset.variant), quantity: 1 };
    // Only when the button carries a plan. Cards add one-time by default, to
    // match the product page; subscribe is offered there and again in the cart.
    if (btn.dataset.plan) body.selling_plan = Number(btn.dataset.plan);

    // Ask for the drawer's markup in the same request. Fetching the cart and
    // then re-rendering the section afterwards meant three round trips before
    // anything appeared; this needs one.
    const cartItems = document.querySelector('cart-items-component');
    const sectionId = cartItems && cartItems.dataset.sectionId;
    if (sectionId) body.sections = sectionId;

    let added;
    try {
      const res = await fetch(shopRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('add failed');
      added = await res.json();
    } catch (err) {
      if (clearPending) clearPending();
      btn.disabled = false;
      btn.textContent = label;
      return;
    }

    // BEFORE the re-render, not after. `cart:update` makes the drawer morph the
    // server's markup onto .cart-drawer__items, and the stand-in row sitting
    // inside that container threw the reconciliation off: morph dropped the real
    // line-item wrapper, and removing the stand-in afterwards left the drawer
    // empty apart from its scripts. Clearing first keeps the container matching
    // what the server rendered.
    if (clearPending) clearPending();

    // source 'product-form-component' makes the cart icon add to its current
    // count rather than replace it, which is what we want for a single add.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: added,
          sourceId: 'kodo-cart-add',
          data: {
            source: 'product-form-component',
            itemCount: (added && added.quantity) || 1,
            sections: added && added.sections,
          },
        },
      })
    );

    // Harmless if the drawer already replaced this node during its re-render.
    btn.disabled = false;
    btn.textContent = label;
  });
})();
